import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/AuthContext";
import { useI18n } from "@/i18n";

const CAPITAL_IMAGES = {
  "New York": "https://images.pexels.com/photos/2190283/pexels-photo-2190283.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Tokyo": "https://images.pexels.com/photos/2506923/pexels-photo-2506923.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Madrid": "https://images.pexels.com/photos/29352449/pexels-photo-29352449.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Sydney": "https://images.pexels.com/photos/2193300/pexels-photo-2193300.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Luxembourg": "https://images.pexels.com/photos/2873669/pexels-photo-2873669.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "St. Petersburg": "https://images.pexels.com/photos/3601425/pexels-photo-3601425.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Cape Town": "https://images.pexels.com/photos/5137965/pexels-photo-5137965.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Maldives": "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Lisbon": "https://images.pexels.com/photos/2549018/pexels-photo-2549018.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "Marrakech": "https://images.pexels.com/photos/4502973/pexels-photo-4502973.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const CAPITALS = ["New York","Tokyo","Madrid","Sydney","Luxembourg","St. Petersburg","Cape Town","Maldives","Lisbon","Marrakech"];

export default function Home() {
  const { t, lang } = useI18n();
  const [recipes, setRecipes] = useState([]);
  useEffect(() => {
    axios.get(`${API}/recipes`).then(r => setRecipes(r.data)).catch(()=>{});
  }, []);
  const featured = recipes.slice(0, 6);
  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden grain min-h-[92vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/18980542/pexels-photo-18980542.png?auto=compress&cs=tinysrgb&dpr=2&h=850&w=1600" alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-32 grid lg:grid-cols-12 gap-12 stagger">
          <div className="lg:col-span-8">
            <div className="overline">{t("hero_overline")}</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mt-6 fade-in">
              <span className="block">{t("hero_title_a")}</span>
              <span className="block italic gold font-light">{t("hero_title_b")}</span>
            </h1>
            <p className="text-zinc-300 text-lg font-light mt-8 max-w-2xl leading-relaxed">{t("hero_sub")}</p>
            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
              <Link to="/recipes" className="btn-gold" data-testid="hero-cta-acquire">
                {t("hero_cta")} <span>→</span>
              </Link>
              <Link to="/bundles" className="btn-ghost" data-testid="hero-cta-bundles">{t("hero_cta_3")} ✦</Link>
              <a href="#capitals" className="btn-ghost" data-testid="hero-cta-capitals">{t("hero_cta_2")}</a>
            </div>
            <div className="mt-16 flex gap-12 text-zinc-400 text-sm">
              <div><div className="font-serif text-3xl gold">10</div><div className="overline mt-1">Capitals</div></div>
              <div><div className="font-serif text-3xl gold">£70</div><div className="overline mt-1">Per meal</div></div>
              <div><div className="font-serif text-3xl gold">£50</div><div className="overline mt-1">Per dessert</div></div>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:block fade-in">
            <div className="glass p-3">
              <img src="https://images.pexels.com/photos/1199957/pexels-photo-1199957.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=620" alt="" className="w-full h-[520px] object-cover" />
              <div className="p-4">
                <div className="overline">Signature</div>
                <div className="font-serif text-xl mt-1">Saffron Lamb Tagine</div>
                <div className="text-zinc-500 text-sm mt-1">Marrakech · Riad Numéro Sept</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capitals */}
      <section id="capitals" className="py-28 relative" data-testid="capitals-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row justify-between md:items-end gap-6">
            <div>
              <div className="overline">{t("capitals_overline")}</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter mt-3">{t("capitals_title")}</h2>
            </div>
            <p className="text-zinc-500 max-w-md">{t("capitals_sub")}</p>
          </div>
          <div className="divider-gold my-10" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 stagger">
            {CAPITALS.map((c) => (
              <Link to={`/recipes?city=${encodeURIComponent(c)}`} key={c} className="relative group overflow-hidden card-recipe" data-testid={`capital-card-${c.toLowerCase().replace(/[^a-z]/g,'-')}`}>
                <img src={CAPITAL_IMAGES[c]} alt={c} className="w-full h-44 object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/30 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="font-serif text-lg leading-tight">{c}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured catalog */}
      <section className="py-20" data-testid="catalog-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row justify-between md:items-end gap-6">
            <div>
              <div className="overline">{t("catalog_overline")}</div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter mt-3">{t("catalog_title")}</h2>
              <p className="text-zinc-500 mt-3 max-w-xl">{t("catalog_sub")}</p>
            </div>
            <Link to="/recipes" className="btn-ghost" data-testid="see-all-btn">View all →</Link>
          </div>
          <div className="divider-gold my-10" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
            {featured.map(r => (
              <RecipeCard key={r.id} r={r} lang={lang} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* Bundles teaser */}
      <section className="py-20 relative" data-testid="bundles-teaser">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="glass relative overflow-hidden grain p-10 lg:p-16 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="overline">{t("bundle_overline")}</div>
              <h2 className="font-serif italic gold text-4xl lg:text-5xl tracking-tighter mt-4 leading-[1.0]">{t("bundle_grand_title")}</h2>
              <p className="text-zinc-300 font-light mt-6 max-w-xl">{t("bundle_sub")}</p>
              <div className="flex flex-wrap gap-6 mt-8 text-zinc-400 text-sm">
                <div><span className="gold font-serif text-3xl">£200</span><div className="overline mt-1">{t("bundle_capital_label")} · pass</div></div>
                <div><span className="gold font-serif text-3xl">£550</span><div className="overline mt-1">Grand Tour</div></div>
              </div>
              <Link to="/bundles" className="btn-gold mt-8" data-testid="bundles-teaser-cta">{t("hero_cta_3")} →</Link>
            </div>
            <div className="lg:col-span-5 hidden lg:block">
              <img src="https://images.pexels.com/photos/3535383/pexels-photo-3535383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=520&w=720" alt="" className="w-full h-[420px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Atelier story */}
      <section id="atelier" className="py-32 relative grain" data-testid="atelier-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline">{t("about_overline")}</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter mt-4 italic gold font-light">{t("about_title")}</h2>
            <p className="text-zinc-300 font-light text-lg leading-relaxed mt-8 max-w-2xl">{t("about_p")}</p>
          </div>
          <div className="lg:col-span-5">
            <img src="https://images.pexels.com/photos/36630804/pexels-photo-36630804.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=720" alt="" className="w-full h-[560px] object-cover" />
          </div>
        </div>
      </section>
    </main>
  );
}

export function RecipeCard({ r, lang, t }) {
  const tr = r[lang] || r.en;
  const price = r.price ?? (r.type === "meal" ? 70 : 50);
  const img = r.image_b64 || r.image_url;
  return (
    <Link to={`/recipes/${r.id}`} className="card-recipe block group" data-testid={`recipe-card-${r.id}`}>
      <div className="aspect-[4/5] overflow-hidden relative">
        {img && <img src={img} alt={tr.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="tag tag-gold" data-testid={`tag-type-${r.type}`}>{r.type === "meal" ? "Meal · £70" : "Dessert · £50"}</span>
        </div>
        <div className="absolute bottom-3 right-3">
          <span className="tag">{r.capital_city}</span>
        </div>
      </div>
      <div className="p-6">
        <div className="overline">{r.capital_city} · {r.capital_country}</div>
        <h3 className="font-serif text-2xl tracking-tight mt-2">{tr.title}</h3>
        <p className="text-zinc-500 italic font-light text-sm mt-2 line-clamp-2">{tr.tagline}</p>
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/5">
          <span className="font-serif text-2xl gold">£{price}</span>
          <span className="overline">{t("cta_view")} →</span>
        </div>
      </div>
    </Link>
  );
}
